import { useCallback } from 'react';
import Papa from 'papaparse';

import { buildVmtRecordFromSelection, VMT_CSV_HEADERS } from '../../utils/vmt';
import {
  copyToClipboard,
  formatVmtDateValue,
  safeExportValue,
  safeTsvCell,
} from '../../utils/productSelectionsExport';

const useProductSelectionsExport = ({
  table,
  selectedRows,
  setSnackbar,
  buildVmtContextFromRow,
  resolveSelectionForStaging,
}) => {
  const handleCopySelected = useCallback(
    async (format) => {
      if (!selectedRows.length) return;
      if (format === 'json') {
        const enrollmentMap = new Map();
        selectedRows.forEach((row) => {
          const source = row.sourceEnrollment || row.source || row;
          if (!source) return;
          const bacKey = source.bac ?? source.dealerCd ?? row.bac ?? '';
          const programKey = source.programCode || source.program || source.programName || row.programCode || row.programName || '';
          const key = `${bacKey}-${programKey}`;
          if (!enrollmentMap.has(key)) enrollmentMap.set(key, source);
        });
        const payload = Array.from(enrollmentMap.values());
        await copyToClipboard(JSON.stringify(payload, null, 2));
        setSnackbar({
          open: true,
          message: `Copied ${payload.length} enrollment${payload.length === 1 ? '' : 's'} as JSON!`,
          severity: 'success',
        });
        return;
      }

      // TSV of visible columns
      const exportableColumns = (table.getAllLeafColumns?.() || []).filter((col) => {
        const id = col?.id;
        if (!id) return false;
        if (id === 'mrt-row-select') return false;
        if (!col.getIsVisible?.()) return false;
        const meta = col.columnDef?.meta || {};
        if (meta.disableExport) return false;
        return Boolean(col.columnDef?.header);
      });
      const headers = exportableColumns.map((c) => c.columnDef.header).join('\t');
      const rowsText = selectedRows
        .map((row) =>
          exportableColumns
            .map((col) => {
              const key = col.columnDef?.accessorKey;
              if (key) return safeTsvCell(row[key]);
              if (typeof col.columnDef?.accessorFn === 'function') return safeTsvCell(col.columnDef.accessorFn(row));
              return safeTsvCell(row[col.id]);
            })
            .join('\t')
        )
        .join('\n');
      await copyToClipboard(`${headers}\n${rowsText}`);
      setSnackbar({ open: true, message: `Copied ${selectedRows.length} selections as TSV!`, severity: 'success' });
    },
    [selectedRows, setSnackbar, table]
  );

  const exportCsvFromRows = useCallback(
    async ({ exportRows, fileName }) => {
      const exportableColumns = (table.getAllLeafColumns?.() || []).filter((col) => {
        const id = col?.id;
        if (!id) return false;
        if (id === 'mrt-row-select') return false;
        if (!col.getIsVisible?.()) return false;
        const meta = col.columnDef?.meta || {};
        if (meta.disableExport) return false;
        return Boolean(col.columnDef?.header);
      });
      if (!exportableColumns.length) {
        setSnackbar({ open: true, message: 'No visible columns to export.', severity: 'info' });
        return;
      }

      const columns = exportableColumns.map((c) => c.id);
      const sanitized = exportRows.map((row) => {
        const out = {};
        exportableColumns.forEach((col) => {
          const key = col.columnDef?.accessorKey;
          let value = '';
          if (key) value = row[key];
          else if (typeof col.columnDef?.accessorFn === 'function') value = col.columnDef.accessorFn(row);
          else value = row[col.id];
          out[col.id] = safeExportValue(value);
        });
        return out;
      });
      const csv = Papa.unparse(sanitized, { quotes: true, columns });

      // Try File System Access API first, fallback to download
      try {
        const { saveCsvFile } = await import('../../utils/fileSystemAccess');
        const result = await saveCsvFile(csv, fileName);
        if (result.success && !result.cancelled) {
          setSnackbar({ open: true, message: `Exported ${exportRows.length} selection(s) to CSV.`, severity: 'success' });
          return;
        }
        if (result.cancelled) return;
      } catch (_err) {
        // noop
      }

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: `Exported ${exportRows.length} selection(s) to CSV.`, severity: 'success' });
    },
    [setSnackbar, table]
  );

  const handleExportVisibleCsv = useCallback(async () => {
    const exportRows = table.getFilteredRowModel?.().rows?.map((r) => r.original) || [];
    if (!exportRows.length) {
      setSnackbar({ open: true, message: 'Nothing to export.', severity: 'info' });
      return;
    }
    const fileName = `selections_export_${new Date().toISOString().split('T')[0]}.csv`;
    await exportCsvFromRows({ exportRows, fileName });
  }, [exportCsvFromRows, setSnackbar, table]);

  const handleExportSelectedCsv = useCallback(async () => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: 'No selections chosen for export.', severity: 'info' });
      return;
    }
    const fileName = `selections_export_${new Date().toISOString().split('T')[0]}.csv`;
    await exportCsvFromRows({ exportRows: selectedRows, fileName });
  }, [exportCsvFromRows, selectedRows, setSnackbar]);

  const handleExportVmtTemplate = useCallback(async () => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: 'No selections chosen for export.', severity: 'info' });
      return;
    }
    const rowsForCsv = selectedRows.map((row) => {
      const selection = resolveSelectionForStaging(row);
      const record = buildVmtRecordFromSelection(selection, buildVmtContextFromRow(row));
      const out = {};
      VMT_CSV_HEADERS.forEach((field) => {
        const raw = record[field] ?? '';
        out[field] = VMT_CSV_HEADERS.includes(field) ? raw : raw;
      });
      // normalize date fields for VMT
      ['billingDate', 'selectionDate', 'effectiveDate'].forEach((f) => {
        if (Object.prototype.hasOwnProperty.call(out, f)) out[f] = formatVmtDateValue(out[f]);
      });
      return out;
    });
    const csv = Papa.unparse(rowsForCsv, { quotes: true, columns: VMT_CSV_HEADERS });
    const fileName = `bulk_send_template_${new Date().toISOString().split('T')[0]}.csv`;

    try {
      const { saveCsvFile } = await import('../../utils/fileSystemAccess');
      const result = await saveCsvFile(csv, fileName);
      if (result.success && !result.cancelled) {
        setSnackbar({ open: true, message: 'Exported VMT template.', severity: 'success' });
        return;
      }
      if (result.cancelled) return;
    } catch (_err) {
      // noop
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: 'Exported VMT template.', severity: 'success' });
  }, [buildVmtContextFromRow, selectedRows, resolveSelectionForStaging, setSnackbar]);

  return {
    handleCopySelected,
    handleExportVisibleCsv,
    handleExportSelectedCsv,
    handleExportVmtTemplate,
  };
};

export default useProductSelectionsExport;
